import { Component, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, combineLatest, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminCard, CardsAdminService } from '../../../services/admin/cards-admin.service';

const PAGE_SIZE = 50;

interface CatalogRequest {
  q: string;
  rarity: string;
  offset: number;
}

@Component({
  selector: 'app-cards-catalog-tab',
  imports: [TranslocoModule, ReactiveFormsModule, DatePipe],
  templateUrl: './cards-catalog-tab.component.html',
  styleUrl: './cards-catalog-tab.component.scss'
})
export class CardsCatalogTabComponent {
  readonly pageSize = PAGE_SIZE;
  readonly rarities = ['common', 'rare', 'legendary', 'mythic'];

  // Reuses the studio tab's rarity translation keys so this tab doesn't grow a second,
  // divergent set of rarity labels.
  readonly rarityLabelKeys: Record<string, string> = {
    common: 'cardsAdminStudioRarityCommon',
    rare: 'cardsAdminStudioRarityRare',
    legendary: 'cardsAdminStudioRarityLegendary',
    mythic: 'cardsAdminStudioRarityMythic'
  };

  search = new FormControl<string>('', { nonNullable: true });
  rarity = new FormControl<string>('', { nonNullable: true });

  rows = signal<AdminCard[]>([]);
  total = signal(0);
  offset = signal(0);

  // Page requests from goTo() are pushed here instead of opening their own
  // subscribe() - see the comment in the constructor for why.
  private pageRequest$ = new Subject<number>();

  constructor(
    private cardsAdmin: CardsAdminService,
    private transloco: TranslocoService,
    private snackBar: MatSnackBar
  ) {
    // Every filter change resets to page 1 - keeping an offset from a longer
    // result set would land the admin on an empty page.
    const filterChange$ = combineLatest([
      this.search.valueChanges.pipe(debounceTime(300), startWith(this.search.value)),
      this.rarity.valueChanges.pipe(startWith(this.rarity.value))
    ]).pipe(
      distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1]),
      map(([q, rarity]): CatalogRequest => ({ q, rarity, offset: 0 }))
    );

    const pageChange$ = this.pageRequest$.pipe(
      map((offset): CatalogRequest => ({ q: this.search.value, rarity: this.rarity.value, offset }))
    );

    // Paging MUST be merged into this same switchMap rather than getting its own
    // subscribe(): a page-2 request left in flight when the admin edits the search
    // box would otherwise resolve AFTER the filtered response and silently overwrite
    // rows/total with unrelated, unfiltered cards while `offset` already reads the
    // filtered value - the admin would then be toggling hidden/no_random_drop/
    // local_only on a card they never meant to touch. Routing both request sources
    // through one switchMap means whichever request is newest - filter or page -
    // cancels whatever else is in flight, so `offset` and the rendered `rows` can
    // never disagree. Do not "simplify" this back into a separate subscribe().
    merge(filterChange$, pageChange$)
      .pipe(
        switchMap((req) => {
          this.offset.set(req.offset);
          return this.cardsAdmin.getCardCatalog$({
            q: req.q,
            rarity: req.rarity,
            limit: PAGE_SIZE,
            offset: req.offset
          });
        }),
        takeUntilDestroyed()
      )
      .subscribe((page) => {
        this.rows.set(page.rows);
        this.total.set(page.total);
      });
  }

  goTo(offset: number): void {
    if (offset < 0 || offset >= this.total()) {
      return;
    }
    this.pageRequest$.next(offset);
  }

  toggleHidden(card: AdminCard, value: boolean): void {
    this.save(card, { hidden: value });
  }

  toggleNoRandomDrop(card: AdminCard, value: boolean): void {
    this.save(card, { noRandomDrop: value });
  }

  toggleLocalOnly(card: AdminCard, value: boolean): void {
    this.save(card, { localOnly: value });
  }

  private save(card: AdminCard, changes: { hidden?: boolean; noRandomDrop?: boolean; localOnly?: boolean }): void {
    // Flip the checkbox optimistically the instant the admin clicks, then reconcile with the
    // server response. Re-emitting the unchanged row on failure would NOT repaint the checkbox -
    // [checked] would still evaluate to the value this optimistic write already applied, so
    // Angular would see no change and leave the box showing the click that never actually saved.
    // Reverting to `previous` (a distinct object) on error forces the real repaint.
    const previous = card;
    const optimistic: AdminCard = { ...card, ...changes };
    this.rows.update((rows) => rows.map((r) => (r.id === card.id ? optimistic : r)));

    this.cardsAdmin.updateCard$(card.id, changes).subscribe({
      next: (updated) => {
        this.rows.update((rows) => rows.map((r) => (r.id === updated.id ? updated : r)));
        this.snackBar.open(this.transloco.translate('cardsAdminCatalogSaved'), '', { duration: 2000 });
      },
      error: () => {
        // The global ErrorsInterceptorService already shows a generic failure snackbar for every
        // status except 401/403 (see errors-interceptor.service.ts) - opening another one here
        // would double-report the same failure, so this handler's only job is undoing the
        // optimistic flip above.
        this.rows.update((rows) => rows.map((r) => (r.id === previous.id ? previous : r)));
      }
    });
  }
}
